import styles from './ExcelExportModalWindow.module.scss';
import Box from '@components/Box';
import cn from 'classnames';
import CompletionModalWindow from '@components/modals/CompletionModalWindow';
import DateRangeInput from '@containers/Filter/DateRangeFilter/DateRangeInput';
import FormErrorMessage from '@components/FormErrorMessage';
import PlacingOrderButtons from '@components/PlacingOrderButtons';
import PlacingOrderHeader from '@components/PlacingOrderHeader';
import PlacingOrderTitle
  from '@components/modals/PlacingOrderModalWindow/components/PlacingOrderTitle';
import TextInputContainer from '@containers/TextInputContainer';
import { buildFilterObject } from '@utils/buildFilterObject';
import { ButtonType } from '@components/Button';
import { Column, Grid, Row } from '@components/Grid';
import { FilterModel } from '@hooks/useFiltersSearchParams';
import { formatDate, formatNumber } from '@utils/formatValues';
import { getParcelsNumberByFilters, TParcelsNumberByFiltersResponse } from '@api/apiClient';
import {
  IExcelExportModalWindowProps,
  TExcelExportEmail,
  TExportExportRange
} from '@src/pages/ParcelsPage/types';
import { Loader } from '@components/Loader';
import { subWeeks, subMonths } from 'date-fns';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useProfile } from '@contexts/ProfileContext';
import { useTranslation } from 'react-i18next';
import { ValueType } from '@containers/Filter/types';

const ExcelExportModalWindow: React.FC<IExcelExportModalWindowProps> = (props: IExcelExportModalWindowProps): React.ReactElement => {
  const {
    errorFileDownloading,
    excelExportFilters,
    handlerDownloadingExcelFile,
    handlerSetErrorFileDownloading,
    handlerSetInitialNumberParcels,
    handlerSetIsOpenExcelExportWindow,
    isFileDownloading,
    limitationExcelExport
  } = props;

  const [dateRangeFilter, setDateRangeFilter] = useState<FilterModel | undefined>(undefined);
  const [excelExportRange, setExcelExportRange] = useState<string>('');
  const [isOpenCompletionWindow, setIsOpenCompletionWindow] = useState<boolean>(false);
  const [showInput, setShowInput] = useState<boolean>(true);

  const { account } = useProfile();
  const { t } = useTranslation();

  useEffect(() => {
    const dateRange: string = 'dateRange';
    const desiredDateRangeFilter: FilterModel | undefined = excelExportFilters.find((item: FilterModel) => item.name === dateRange);
    if (desiredDateRangeFilter) {
      setDateRangeFilter(desiredDateRangeFilter);
      setExcelExportRange('range');
    } else {
      setDateRangeFilter({ name: dateRange, value: ['', ''] });
      setExcelExportRange('allTime');
    }
  }, [excelExportFilters]);

  const {
    error: errorGetParcelsNumberByFilters,
    isError: isErrorGetParcelsNumberByFilters,
    isLoading: isLoadingGetParcelsNumberByFilters,
    mutate: mutateGetParcelsNumberByFilters,
    reset: resetGetParcelsNumberByFilters
  } = useMutation({
    mutationFn: getParcelsNumberByFilters,
    onSuccess: (result: TParcelsNumberByFiltersResponse) => {
      handlerSetInitialNumberParcels(result.parcelsCount);
      setShowInput(result.parcelsCount >= limitationExcelExport);
    }
  });

  useEffect(() => {
    return () => {
      handlerSetErrorFileDownloading(undefined);
      resetGetParcelsNumberByFilters();
    };
  }, []);

  const { control, handleSubmit, watch } = useForm<TExcelExportEmail>({
    defaultValues: {
      email: account?.email ?? ''
    },
    mode: 'onChange'
  });

  const email = watch('email');

  const excelExportRanges: TExportExportRange[] = [
    {
      name: t('excelExportModalWindow.rangePicker.week') as string,
      value: 'week'
    },
    {
      name: t('excelExportModalWindow.rangePicker.month') as string,
      value: 'month'
    },
    {
      name: t('excelExportModalWindow.rangePicker.allTime') as string,
      value: 'allTime'
    }
  ];

  const onClickRangeBtn = (range: 'week' | 'month' | 'allTime'): void => {
    const dateRange: string = 'dateRange';
    const desiredDateRangeFilter: FilterModel | undefined = excelExportFilters.find((item: FilterModel) => item.name === dateRange);
    const today = new Date();
    let startDate: Date | undefined;
    switch (range) {
      case 'week':
        startDate = subWeeks(today, 1);
        break;
      case 'month':
        startDate = subMonths(today, 1);
        break;
    }
    const formattedRange = startDate ? [formatDate(startDate, 'yyyy-MM-dd'), formatDate(today, 'yyyy-MM-dd')] : ['', ''];
    const newDateRangeFilter: FilterModel = { name: dateRange, value: formattedRange };
    const updatedFilters: FilterModel[] =
        desiredDateRangeFilter
            ? excelExportFilters.map((item: FilterModel) => item.name === dateRange ? newDateRangeFilter : item)
            : [...excelExportFilters, newDateRangeFilter];
    handlerSetErrorFileDownloading(undefined);
    resetGetParcelsNumberByFilters();
    mutateGetParcelsNumberByFilters(buildFilterObject(updatedFilters));
    setDateRangeFilter(
        desiredDateRangeFilter
            ? { ...desiredDateRangeFilter, value: newDateRangeFilter.value }
            : newDateRangeFilter
    );
    setExcelExportRange(range);
  };

  const onChangeRangePicker = (selection: ValueType[]): void => {
    const dateRange: string = 'dateRange';
    const desiredDateRangeFilter: FilterModel | undefined = excelExportFilters.find((item: FilterModel) => item.name === dateRange);
    const [startDate, endDate] = selection;
    let formattedRange: string[];
    if (startDate && endDate) {
      formattedRange = [
        formatDate(startDate, 'yyyy-MM-dd'),
        formatDate(endDate, 'yyyy-MM-dd')
      ];
      const newDateRangeFilter: FilterModel = { name: dateRange, value: formattedRange };
      const updatedFilters: FilterModel[] =
          desiredDateRangeFilter
              ? excelExportFilters.map((item: FilterModel) => item.name === dateRange ? newDateRangeFilter : item)
              : [...excelExportFilters, newDateRangeFilter];
      handlerSetErrorFileDownloading(undefined);
      resetGetParcelsNumberByFilters();
      mutateGetParcelsNumberByFilters(buildFilterObject(updatedFilters));
      setDateRangeFilter(
          desiredDateRangeFilter
              ? { ...desiredDateRangeFilter, value: newDateRangeFilter.value }
              : newDateRangeFilter
      );
      setExcelExportRange('range');
    } else {
      setExcelExportRange('');
    }
  };

  const onSubmit = (form: TExcelExportEmail) => {
    const dateRange: string = 'dateRange';
    const desiredDateRangeFilter: FilterModel | undefined = excelExportFilters.find((item: FilterModel) => item.name === dateRange);
    const finalFilters: FilterModel[] =
        desiredDateRangeFilter
            ? excelExportFilters.map((item: FilterModel) => item.name === dateRange ? dateRangeFilter! : item)
            : [...excelExportFilters, dateRangeFilter!];
    const params: FilterModel[] =
        showInput
            ? [...finalFilters, { name: 'recipientEmail', value: form.email }]
            : finalFilters;
    handlerDownloadingExcelFile(params);
    showInput && setIsOpenCompletionWindow(true);
  };

  if (isOpenCompletionWindow) {
    return (
        <CompletionModalWindow
            btnText={t('excelExportModalWindow.completion.btnText')}
            description={email}
            handlerSetIsOpenModalWindow={() => handlerSetIsOpenExcelExportWindow(false)}
            title={t('excelExportModalWindow.completion.title')}
        />
    );
  }

  return (
      <Grid>
        <PlacingOrderHeader title={t('excelExportModalWindow.headline')}/>
        <PlacingOrderTitle title={t('excelExportModalWindow.title')}/>
        <Row>
          <Column phone={12}>
            <Box mb={8}>
              <div className={styles.ranges}>
                {excelExportRanges.map((item: TExportExportRange, index: number) => (
                        <button
                            className={cn(styles.rangeItemBtn, item.value === excelExportRange && styles.rangeItemBtnSelected)}
                            disabled={item.value === excelExportRange}
                            key={index}
                            onClick={() => onClickRangeBtn(item.value)}
                        >
                          {item.name}
                        </button>
                    )
                )}
                <DateRangeInput
                    customClass={cn(styles.pickerBtn, excelExportRange === 'range' && styles.pickerBtnSelected)}
                    customDateFormat="dd.MM.yyyy"
                    onChange={onChangeRangePicker}
                    placeholder={t('excelExportModalWindow.rangePicker.selectRange')}
                    showTabs={false}
                    value={[
                      excelExportRange === 'range' && dateRangeFilter?.value?.[0] ? new Date(dateRangeFilter.value[0]) : null,
                      excelExportRange === 'range' && dateRangeFilter?.value?.[1] ? new Date(dateRangeFilter.value[1]) : null
                    ]}
                />
              </div>
            </Box>
          </Column>
        </Row>
        {isFileDownloading ? (
            <Row>
              <Column phone={12}>
                <Box mb={5}>
                  <div className={styles.plate}>
                    <Loader
                        dark
                        size="xs"
                    />
                    {t('excelExportModalWindow.excelExportPlate')}
                  </div>
                </Box>
              </Column>
            </Row>
        ) : (
            showInput && (
                <Row>
                  <Column phone={12}>
                    <Box mb={5}>
                      <div className={styles.field}>
                        <span className={styles.inputPlate}>
                          {t('excelExportModalWindow.inputPlate', { limit: formatNumber(limitationExcelExport) })}
                        </span>
                        <TextInputContainer
                            control={control}
                            name="email"
                            rules={{ required: t('defaultFormErrors.required') }}
                            title={t('excelExportModalWindow.input')}
                            type="email"
                        />
                      </div>
                    </Box>
                  </Column>
                </Row>
            )
        )}
        <PlacingOrderButtons
            buttons={[
              {
                action: () => handlerSetIsOpenExcelExportWindow(false),
                text: t('excelExportModalWindow.cancel') as string,
                type: ButtonType.SKELETON
              }, {
                action: handleSubmit(onSubmit),
                disabled: isFileDownloading || isLoadingGetParcelsNumberByFilters || !excelExportRange.length || (showInput && !email),
                text: t('excelExportModalWindow.unload') as string,
                type: ButtonType.BLOCK
              }
            ]}
        />
        {isErrorGetParcelsNumberByFilters &&
            <FormErrorMessage error={errorGetParcelsNumberByFilters}/>}
        {errorFileDownloading && <FormErrorMessage error={errorFileDownloading}/>}
      </Grid>
  );

};

export default ExcelExportModalWindow;
